import { useMemo, useState } from "react";
import { ArrowLeft, Check, FileText, Minus, X } from "lucide-react";
import { useToast } from "@/shell/Toast";
import DemoStudentProfile, { DEMO_PROFILES } from "@/components/demo/DemoStudentProfile";

/*
 * Results for one item in the guided demo: who turned it in, the class
 * average, the score spread, the most-missed questions (from the item's real
 * questions) and every student. The class is 24 sample students made up on
 * the spot from the item id, so the numbers are steady between visits.
 * Nothing is saved or sent.
 */

export type ResultsQuestion = {
  id: string;
  prompt: string;
  points?: number | null;
  options?: { id: number; label: string }[] | null;
  correct?: { optionId?: number; optionIds?: number[] } | null;
};

export type ResultsKind = "quiz" | "upload" | "discussion";

type Status = "turned-in" | "in-progress" | "not-started" | "missing" | "late" | "needs-grading" | "graded" | "no-reply";

type Answer = { ok: boolean; picks: number[]; answered: boolean };

type SampleStudent = {
  id: string;
  name: string;
  initials: string;
  tone: string;
  status: Status;
  answers: Answer[];
  score: number;
  tries: number;
  when: string;
  grade: number | null;
  reply: string;
};

const NAMES = [
  "Maya R.", "Jordan T.", "Ava L.", "Diego M.", "Noah K.", "Sofia G.", "Ethan W.", "Chloe B.",
  "Liam P.", "Isabella N.", "Mason H.", "Amara O.", "Lucas F.", "Zoe C.", "Elijah S.", "Mia D.",
  "Aiden J.", "Harper V.", "Caleb Y.", "Layla A.", "Owen E.", "Nora I.", "Gabriel U.", "Ella Q.",
];
const IDS = ["maya", "jordan", "ava", "diego"];
const TONES = ["teal", "amber", "violet", "green", "pink"];
const WHEN = ["Mon 3:12 PM", "Mon 8:40 PM", "Tue 7:05 AM", "Tue 4:30 PM", "Sun 6:18 PM", "Mon 5:02 PM"];
/** The four sample students the discussion and profiles already use. */
const KNOWN_ABILITY = [0.3, 0.12, -0.35, 0.18];
const SAMPLE_REPLIES = [
  "My phone bill is $30 a month plus $5 for every gigabyte, so 30 + 5g. I picked g because it stands for gigabytes.",
  "In basketball my points are 2s + 3t. s is two-pointers and t is threes, because the letters match the words.",
  "I just use x for everything.",
  "A ride costs 2.50 + 1.75m, where m is the miles. I used m so I would not forget what it means.",
  "At the store, apples cost $1.20 each, so a apples cost 1.20a.",
  "My bus pass is $2 a ride, so r rides cost 2r.",
];

const STATUS_LABEL: Record<Status, string> = {
  "turned-in": "Turned in",
  "in-progress": "In progress",
  "not-started": "Not started",
  missing: "Missing",
  late: "Late",
  "needs-grading": "Needs grading",
  graded: "Graded",
  "no-reply": "No reply",
};

/** A steady number in [0, 1) from a seed and two indexes. */
function rand(seed: number, a: number, b: number) {
  const x = Math.sin(seed * 0.0013 + a * 12.9898 + b * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function seedOf(text: string) {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) % 100003;
  return h;
}

function correctIds(q: ResultsQuestion) {
  return q.correct?.optionIds?.length ? q.correct.optionIds : q.correct?.optionId != null ? [q.correct.optionId] : [];
}

function buildClass(itemId: string, title: string, kind: ResultsKind, questions: ResultsQuestion[]): SampleStudent[] {
  const seed = seedOf(itemId);
  // Each question gets a share of students who get it right; the hardest one sits near half.
  const draws = questions.map((_, k) => rand(seed, 7, k));
  const hardest = draws.indexOf(Math.min(...draws));
  const share = draws.map((r, k) => (k === hardest ? 0.5 : 0.97 - 0.36 * r));
  const practice = /practice/i.test(title);

  return NAMES.map((name, i) => {
    const ability = i < 4 ? KNOWN_ABILITY[i] : (rand(seed, i, 99) - 0.45) * 0.5 + 0.12;
    let status: Status;
    if (kind === "quiz") {
      status = i === 4 || i === 13 ? "not-started" : i === 9 || i === 17 || i === 21 ? "in-progress" : "turned-in";
      if (practice && i === 2) status = "not-started";
    } else if (kind === "upload") {
      status = i === 1 || i === 2 || i === 4 || i === 13 || i === 18 || i === 22 ? "missing" : i === 7 || i === 15 ? "late" : i === 0 || i === 3 ? "graded" : "needs-grading";
    } else {
      status = i === 4 || i === 13 || i === 20 ? "no-reply" : i === 0 ? "graded" : "needs-grading";
    }
    const answers: Answer[] = questions.map((q, k) => {
      const answered = status === "turned-in" || (status === "in-progress" && k < Math.ceil(questions.length / 2));
      const ok = answered && rand(seed, i, k) < Math.min(0.99, Math.max(0.05, share[k] + ability));
      const right = correctIds(q);
      const wrong = (q.options || []).map((o) => o.id).filter((id) => !right.includes(id));
      // Most students who miss it pick the same wrong option, the rest spread out.
      const lead = wrong[Math.floor(rand(seed, 3, k) * wrong.length)] ?? null;
      const pickWrong = wrong.length ? (rand(seed, k, i + 50) < 0.62 ? lead : wrong[Math.floor(rand(seed, i, k + 9) * wrong.length)]) : null;
      return { ok, answered, picks: !answered ? [] : ok ? right : pickWrong != null ? [pickWrong] : [] };
    });
    const score = questions.reduce((sum, q, k) => sum + (answers[k].ok ? Number(q.points) || 1 : 0), 0);
    return {
      id: i < 4 ? IDS[i] : `s${i}`,
      name,
      initials: name.replace(".", "").split(" ").map((part) => part[0]).join(""),
      tone: TONES[i % TONES.length],
      status,
      answers,
      score,
      tries: status === "turned-in" ? (rand(seed, i, 77) < 0.3 ? 2 : 1) : status === "in-progress" ? 1 : 0,
      when: status === "not-started" || status === "missing" || status === "no-reply" || status === "in-progress" ? "—" : WHEN[i % WHEN.length],
      grade: status === "graded" ? (i === 0 ? (kind === "discussion" ? 9 : 10) : 9) : null,
      reply: SAMPLE_REPLIES[i % SAMPLE_REPLIES.length],
    };
  });
}

type Filter = "all" | "out" | "low" | "grade";

export default function DemoItemResults({
  itemId,
  title,
  kind,
  points,
  questions,
  course,
}: {
  itemId: string;
  title: string;
  kind: ResultsKind;
  points: number;
  questions: ResultsQuestion[];
  course: string;
}) {
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>("all");
  const [openQ, setOpenQ] = useState<string | null>(null);
  const [studentId, setStudentId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const isQuiz = kind === "quiz" && questions.length > 0;
  const maxScore = isQuiz ? questions.reduce((sum, q) => sum + (Number(q.points) || 1), 0) : points || 10;
  const students = useMemo(() => buildClass(itemId, title, kind, isQuiz ? questions : []), [itemId, title, kind, isQuiz, questions]);

  const done = students.filter((s) => s.status === "turned-in");
  const average = done.length ? done.reduce((sum, s) => sum + s.score, 0) / done.length : 0;
  const isLow = (s: SampleStudent) => s.status === "turned-in" && s.score / maxScore < 0.7;
  const below = done.filter(isLow);
  const out = students.filter((s) => ["not-started", "in-progress", "missing", "no-reply"].includes(s.status));
  const waiting = students.filter((s) => s.status === "needs-grading" || s.status === "late");
  const turnedIn = students.length - out.length;
  const graded = students.filter((s) => s.status === "graded");

  const tiles = isQuiz
    ? [
        { big: `${done.length} of ${students.length}`, label: "turned in" },
        { big: `${Math.round((average / maxScore) * 100)}%`, label: `class average (${average.toFixed(1)} / ${maxScore})` },
        { big: `${Math.max(6, Math.round(questions.length * 1.8))} min`, label: "typical time spent" },
        { big: String(below.length), label: "scored below 70%" },
      ]
    : [
        { big: `${turnedIn} of ${students.length}`, label: kind === "discussion" ? "replied" : "turned in" },
        { big: String(waiting.length), label: "waiting for your grade" },
        kind === "discussion"
          ? { big: "38", label: "replies to classmates" }
          : { big: String(students.filter((s) => s.status === "late").length), label: "turned in late" },
        { big: String(graded.length), label: "graded so far" },
      ];

  const bands: [string, number, number][] = [["0–59", 0, 0.6], ["60–69", 0.6, 0.7], ["70–79", 0.7, 0.8], ["80–89", 0.8, 0.9], ["90–100", 0.9, 1.01]];
  const bandCounts = bands.map(([, lo, hi]) => done.filter((s) => s.score / maxScore >= lo && s.score / maxScore < hi).length);
  const bandMax = Math.max(1, ...bandCounts);

  const questionRows = isQuiz
    ? questions
        .map((q, k) => {
          const right = done.filter((s) => s.answers[k].ok).length;
          return { q, k, right, share: done.length ? right / done.length : 0 };
        })
        .sort((a, b) => a.share - b.share || a.k - b.k)
    : [];

  const filters: { id: Filter; label: string }[] = isQuiz
    ? [
        { id: "all", label: `All (${students.length})` },
        { id: "out", label: `Not turned in (${out.length})` },
        { id: "low", label: `Below 70% (${below.length})` },
      ]
    : [
        { id: "all", label: `All (${students.length})` },
        { id: "out", label: `${kind === "discussion" ? "No reply" : "Missing"} (${out.length})` },
        { id: "grade", label: `Needs grading (${waiting.length})` },
      ];
  const shown = students.filter((s) =>
    filter === "out" ? out.includes(s) : filter === "low" ? isLow(s) : filter === "grade" ? waiting.includes(s) : true,
  );

  const student = studentId ? students.find((s) => s.id === studentId) || null : null;
  const profile = profileId ? DEMO_PROFILES[profileId] : null;
  const statusOf = (s: SampleStudent): { label: string; tone: string } => {
    if (isQuiz && isLow(s)) return { label: "Below 70%", tone: "low" };
    return { label: STATUS_LABEL[s.status], tone: s.status };
  };
  const scoreOf = (s: SampleStudent) =>
    isQuiz ? (s.status === "turned-in" ? `${s.score} / ${maxScore}` : "—") : s.grade != null ? `${s.grade} / ${maxScore}` : "—";

  const remind = () => toast(`Reminder sent to ${out.length} students. Demo only, nothing was sent.`);

  if (student) {
    return (
      <div className="al-res">
        <button type="button" className="al-demo-btn al-demo-btn--ghost al-res-back" onClick={() => setStudentId(null)}>
          <ArrowLeft size={15} aria-hidden="true" />
          All results
        </button>
        <div className="al-res-student">
          <span className={`al-demo-ava al-demo-ava--${student.tone}`} aria-hidden="true">
            {student.initials}
          </span>
          <div className="al-res-student-who">
            <p className="al-res-student-name">{student.name}</p>
            <p className="al-res-student-line">
              {statusOf(student).label}
              {scoreOf(student) !== "—" ? ` · ${scoreOf(student)}` : ""}
              {student.tries ? ` · ${student.tries} ${student.tries === 1 ? "try" : "tries"}` : ""}
              {student.when !== "—" ? ` · ${student.when}` : ""}
            </p>
          </div>
          {DEMO_PROFILES[student.id] ? (
            <button type="button" className="al-demo-btn al-demo-btn--ghost" onClick={() => setProfileId(student.id)}>
              Open profile
            </button>
          ) : null}
        </div>

        {isQuiz ? (
          <section className="al-res-card" aria-label="Their answers">
            <h3 className="al-res-cap">Their answers</h3>
            <ol className="al-res-answers">
              {questions.map((q, k) => {
                const a = student.answers[k];
                const right = correctIds(q);
                const label = (ids: number[]) =>
                  ids.map((id) => q.options?.find((o) => o.id === id)?.label).filter(Boolean).join(", ");
                return (
                  <li key={q.id}>
                    <span className={`al-res-mark ${!a.answered ? "is-none" : a.ok ? "is-ok" : "is-bad"}`} aria-hidden="true">
                      {!a.answered ? <Minus size={13} /> : a.ok ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
                    </span>
                    <span>
                      <strong>Q{k + 1}.</strong> {q.prompt}
                      <span className="al-res-answer-note">
                        {!a.answered
                          ? "Not answered yet"
                          : a.ok
                            ? `Right${right.length ? `: ${label(right)}` : ""}`
                            : a.picks.length
                              ? `Answered ${label(a.picks)} · correct: ${label(right)}`
                              : "Wrong"}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        ) : (
          <section className="al-res-card" aria-label={kind === "discussion" ? "Their reply" : "Their upload"}>
            <h3 className="al-res-cap">{kind === "discussion" ? "Their reply" : "Their upload"}</h3>
            {kind === "discussion" ? (
              <p className="al-res-work">{student.status === "no-reply" ? "No reply yet." : `“${student.reply}”`}</p>
            ) : (
              <p className="al-res-work">
                {student.status === "missing" ? (
                  "Nothing uploaded yet."
                ) : (
                  <>
                    <FileText size={15} aria-hidden="true" /> {student.name.split(" ")[0]} — {title} worksheet.pdf
                    {student.status === "late" ? " · turned in 1 day late" : ""}
                  </>
                )}
              </p>
            )}
            {student.status === "graded" ? (
              <p className="al-res-graded">Graded {scoreOf(student)}</p>
            ) : student.status === "needs-grading" || student.status === "late" ? (
              <p className="al-res-graded is-wait">Waiting for your grade. Grade it from the item, the same way as the discussion.</p>
            ) : null}
          </section>
        )}

        {profile ? (
          <DemoStudentProfile
            student={profile}
            course={course}
            onClose={() => setProfileId(null)}
            onMessageStudent={() => {
              setProfileId(null);
              toast(`In the real app this opens a message to ${profile.firstName}.`);
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div className="al-res">
      <div className="al-res-tiles">
        {tiles.map((tile) => (
          <div key={tile.label} className="al-res-tile">
            <strong>{tile.big}</strong>
            <span>{tile.label}</span>
          </div>
        ))}
      </div>

      {isQuiz ? (
        <div className="al-res-split">
          <section className="al-res-card al-res-scores" aria-label="Score spread">
            <h3 className="al-res-cap">Scores</h3>
            <div className="al-res-bars" role="list">
              {bands.map(([label], i) => (
                <div
                  key={label}
                  className="al-res-bar"
                  role="listitem"
                  aria-label={`${bandCounts[i]} ${bandCounts[i] === 1 ? "student" : "students"} scored ${label}%`}
                  title={`${bandCounts[i]} ${bandCounts[i] === 1 ? "student" : "students"} scored ${label}%`}
                >
                  <em>{bandCounts[i]}</em>
                  <i style={{ height: `${Math.max(3, Math.round((bandCounts[i] / bandMax) * 104))}px` }} />
                </div>
              ))}
            </div>
            <div className="al-res-bar-labels" aria-hidden="true">
              {bands.map(([label]) => (
                <span key={label}>{label}%</span>
              ))}
            </div>
          </section>

          <section className="al-res-card al-res-qs" aria-label="Questions, most missed first">
            <h3 className="al-res-cap">Questions, most missed first</h3>
            <ul className="al-res-qlist">
              {questionRows.map((row, index) => {
                const open = openQ === row.q.id;
                const right = correctIds(row.q);
                const picks = (row.q.options || []).map((option) => ({
                  option,
                  count: done.filter((s) => s.answers[row.k].picks.includes(option.id)).length,
                  right: right.includes(option.id),
                }));
                const topWrong = picks.filter((p) => !p.right).sort((a, b) => b.count - a.count)[0];
                const missed = done.filter((s) => !s.answers[row.k].ok).map((s) => s.name);
                const pct = Math.round(row.share * 100);
                return (
                  <li key={row.q.id}>
                    <button
                      type="button"
                      className={`al-res-q${open ? " is-open" : ""}`}
                      aria-expanded={open}
                      onClick={() => setOpenQ(open ? null : row.q.id)}
                    >
                      <span className="al-res-qn">Q{row.k + 1}</span>
                      <span className="al-res-qtext">{row.q.prompt}</span>
                      {index === 0 && pct < 80 ? <span className="al-res-flag">Most missed</span> : null}
                      <span className="al-res-track" title={`${row.right} of ${done.length} got it right`}>
                        <span className={pct < 60 ? "is-low" : ""} style={{ width: `${pct}%` }} />
                      </span>
                      <span className="al-res-pct">{pct}% right</span>
                    </button>
                    {open ? (
                      <div className="al-res-qmore">
                        {picks.length ? (
                          <ul className="al-res-picks">
                            {picks.map((p) => (
                              <li key={p.option.id}>
                                <span className={`al-res-pick-label${p.right ? " is-right" : ""}`}>{p.option.label}</span>
                                <span className="al-res-track al-res-track--wide">
                                  <span className={p.right ? "" : "is-grey"} style={{ width: `${done.length ? Math.round((p.count / done.length) * 100) : 0}%` }} />
                                </span>
                                <span className="al-res-pick-count">
                                  {p.count}
                                  {p.right ? " · correct" : p === topWrong && p.count > 0 ? " · most common wrong answer" : ""}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        <p className="al-res-missed">Missed it: {missed.length ? missed.join(", ") : "nobody"}</p>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      ) : null}

      <section className="al-res-card al-res-people" aria-label="Students">
        <div className="al-res-people-head">
          <h3 className="al-res-cap">Students</h3>
          <div className="al-res-filters" role="group" aria-label="Show">
            {filters.map((f) => (
              <button key={f.id} type="button" className={`al-res-filter${filter === f.id ? " is-on" : ""}`} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
          {out.length ? (
            <button type="button" className="al-demo-btn al-demo-btn--ghost al-res-remind" onClick={remind}>
              Remind the {out.length} who haven't {kind === "discussion" ? "replied" : "turned it in"}
            </button>
          ) : null}
        </div>
        <div className="al-res-table" role="table" aria-label="Students">
          <div className="al-res-tr al-res-th" role="row">
            <span role="columnheader">Student</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">{isQuiz ? "Score" : "Grade"}</span>
            <span role="columnheader">Tries</span>
            <span role="columnheader">Turned in</span>
          </div>
          {shown.map((s) => {
            const status = statusOf(s);
            return (
              <button key={s.id} type="button" className="al-res-tr" role="row" onClick={() => setStudentId(s.id)}>
                <span role="cell" className="al-res-name">
                  <span className={`al-demo-ava al-demo-ava--${s.tone} al-res-ava`} aria-hidden="true">
                    {s.initials}
                  </span>
                  {s.name}
                </span>
                <span role="cell">
                  <span className={`al-res-status al-res-status--${status.tone}`}>{status.label}</span>
                </span>
                <span role="cell" className={`al-res-score${isQuiz && isLow(s) ? " is-low" : ""}`}>
                  {scoreOf(s)}
                </span>
                <span role="cell">{isQuiz ? s.tries : s.when === "—" ? 0 : 1}</span>
                <span role="cell" className="al-res-when">
                  {s.when}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}
